import React, { useState, useEffect } from "react";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import DataTable from "examples/Tables/DataTable";
import NotificationModal from "./components/NotificationModal";
import ActionModal from "components/Modals/ActionModal";
import { Card, IconButton } from "@mui/material";
import { getNotifications, deleteNotification } from "services/notificationService";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { toast } from "react-toastify";
import Chip from "@mui/material/Chip";
import DeleteIcon from "@mui/icons-material/Delete";

function Notifications() {
  const [openModal, setOpenModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState(null);

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusChip = (notification) => {
    if (notification.scheduledAt) {
      const scheduledDate = new Date(notification.scheduledAt);
      const now = new Date();
      if (scheduledDate > now) {
        return (
          <Chip
            label="Scheduled"
            color="warning"
            size="small"
            sx={{ fontWeight: "bold" }}
          />
        );
      }
    }
    return (
      <Chip
        label="Sent"
        color="success"
        size="small"
        sx={{ fontWeight: "bold" }}
      />
    );
  };

  const isPendingScheduled = (notification) => {
    // Check if notification is scheduled and not yet sent
    if (notification.scheduledAt && !notification.sentAt) {
      const scheduledDate = new Date(notification.scheduledAt);
      const now = new Date();
      return scheduledDate > now;
    }
    return false;
  };

  const handleDelete = (id) => {
    setSelectedNotificationId(id);
    setDeleteModal(true);
  };

  const confirmDelete = () => {
    deleteNotification(selectedNotificationId)
      .then((data) => {
        toast.success("Notification deleted successfully");
        setRefresh(!refresh);
        setDeleteModal(false);
        setSelectedNotificationId(null);
      })
      .catch((err) => {
        console.error("Error deleting notification:", err);
        toast.error(
          err.response?.data?.message || "Failed to delete notification"
        );
        setDeleteModal(false);
        setSelectedNotificationId(null);
      });
  };

  const cancelDelete = () => {
    setDeleteModal(false);
    setSelectedNotificationId(null);
  };

  useEffect(() => {
    setLoading(true);
    getNotifications()
      .then((data) => {
        if (data.success) {
          setNotifications(data.data || []);
        } else {
          toast.error("Failed to fetch notifications");
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        toast.error("Failed to fetch notifications");
        setLoading(false);
      });
  }, [refresh]);

  const columns = [
    {
      Header: "Title",
      accessor: "title",
      maxWidth: 200,
      Cell: ({ value }) => (
        <Tooltip title={value}>
          <span
            style={{
              display: "inline-block",
              maxWidth: 180,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: "bold",
            }}
          >
            {value}
          </span>
        </Tooltip>
      ),
    },
    {
      Header: "Description",
      accessor: "description",
      maxWidth: 300,
      Cell: ({ value }) => (
        <Tooltip title={value}>
          <span
            style={{
              display: "inline-block",
              maxWidth: 280,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: "bold",
            }}
          >
            {value}
          </span>
        </Tooltip>
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      maxWidth: 120,
      Cell: ({ row }) => getStatusChip(row.original),
    },
    {
      Header: "Sent At",
      accessor: "sentAt",
      maxWidth: 180,
      Cell: ({ row }) => (
        <span style={{ fontWeight: "bold" }}>
          {row.original.sentAt
            ? formatDate(row.original.sentAt)
            : row.original.scheduledAt
            ? `Scheduled: ${formatDate(row.original.scheduledAt)}`
            : "N/A"}
        </span>
      ),
    },
    {
      Header: "Action",
      accessor: "action",
      maxWidth: 80,
      Cell: ({ row }) => {
        const isPending = isPendingScheduled(row.original);
        if (!isPending) {
          return null;
        }
        return (
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(row.original._id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        );
      },
    },
  ];

  return (
    <>
      <ArgonBox display="flex" justifyContent="flex-end" py={3}>
        <ArgonButton
          variant="outlined"
          color="#ffffff"
          onClick={handleOpenModal}
        >
          Send Notification
        </ArgonButton>
      </ArgonBox>

      <ArgonBox>
        <Card sx={{ overflow: "visible", padding: 3 }}>
          <ArgonBox
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            p={3}
          >
            <ArgonTypography variant="h5" fontWeight="medium">
              Notification History
            </ArgonTypography>
          </ArgonBox>

          {loading ? (
            <ArgonBox display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </ArgonBox>
          ) : notifications.length > 0 ? (
            <ArgonBox
              sx={{
                "& .MuiTableRow-root": {
                  height: "48px",
                },
                "& .MuiTableCell-root": {
                  padding: "8px 16px",
                  fontSize: "14px",
                },
                "& .MuiTableHead-root .MuiTableCell-root": {
                  padding: "12px 16px",
                  fontSize: "13px",
                  fontWeight: "600",
                },
              }}
            >
              <DataTable
                table={{ columns, rows: notifications }}
                entriesPerPage={false}
                canSearch={false}
                showTotalEntries={false}
                pagination={false}
                customWidth={true}
                cellWidth={true}
              />
            </ArgonBox>
          ) : (
            <ArgonBox display="flex" justifyContent="center" p={3}>
              <ArgonTypography variant="h6">
                No notifications found
              </ArgonTypography>
            </ArgonBox>
          )}
        </Card>
      </ArgonBox>

      {openModal && (
        <NotificationModal
          open={openModal}
          handleClose={handleCloseModal}
          onUpdate={() => setRefresh(!refresh)}
        />
      )}

      {deleteModal && (
        <ActionModal
          open={deleteModal}
          handleClose={cancelDelete}
          title="Delete Scheduled Notification"
          detail="Are you sure you want to delete this scheduled notification? This action cannot be undone."
          handleYes={confirmDelete}
          handleNo={cancelDelete}
          color="#e53e3e"
        />
      )}
    </>
  );
}

export default Notifications;
